/**
 * Nursing homes domain — React Query hooks.
 *
 * Read hooks are re-exported directly from the generated client (with a
 * hand-written detail wrapper where the legacy hook shape relied on a
 * 404-skipping retry predicate).
 *
 * Write hooks go through `useInvalidatingMutation` so cache invalidation
 * is declarative and compile-time-enforced via the `NursingHomeMutationId`
 * registry in `./invalidation`.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'

import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'

// ── Generated query-hook re-exports ────────────────────────────────

export {
  useListNursingHomes,
  listNursingHomesQueryKey,
  useGetNursingHomesSummary,
  getNursingHomesSummaryQueryKey,
  getNursingHomeQueryKey,
} from '../generated/hooks/nursingHomeController'

// ── Generated client-function re-exports (imperative consumers) ────

export { listNursingHomes } from '../generated/clients/listNursingHomes'
export { getNursingHomesSummary } from '../generated/clients/getNursingHomesSummary'

// ── Generated query/mutation type re-exports ───────────────────────

export type {
  ListNursingHomesQueryResponse,
  ListNursingHomesQueryParams,
} from '../generated/types/ListNursingHomes'
export type {
  GetNursingHomeQueryResponse,
  GetNursingHomePathParams,
} from '../generated/types/GetNursingHome'
export type { GetNursingHomesSummaryQueryResponse } from '../generated/types/GetNursingHomesSummary'
export type {
  BulkTagsNursingHomesMutationRequest,
  BulkTagsNursingHomesMutationResponse,
} from '../generated/types/BulkTagsNursingHomes'
export type {
  BulkDistributorsNursingHomesMutationRequest,
  BulkDistributorsNursingHomesMutationResponse,
} from '../generated/types/BulkDistributorsNursingHomes'
export type {
  BulkGposNursingHomesMutationRequest,
  BulkGposNursingHomesMutationResponse,
} from '../generated/types/BulkGposNursingHomes'
export type {
  BulkDealsNursingHomesMutationRequest,
  BulkDealsNursingHomesMutationResponse,
} from '../generated/types/BulkDealsNursingHomes'
export type {
  AssignNursingHomesToCampaignMutationRequest,
  AssignNursingHomesToCampaignMutationResponse,
} from '../generated/types/AssignNursingHomesToCampaign'
export type {
  ExportNursingHomesV2MutationRequest,
  ExportNursingHomesV2MutationResponse,
} from '../generated/types/ExportNursingHomesV2'

// ── Internal imports for hand-written wrappers + mutations ─────────

import { getNursingHomeQueryKey as _getNursingHomeQueryKey } from '../generated/hooks/nursingHomeController'
import { getNursingHome } from '../generated/clients/getNursingHome'
import { bulkTagsNursingHomes } from '../generated/clients/bulkTagsNursingHomes'
import { bulkDistributorsNursingHomes } from '../generated/clients/bulkDistributorsNursingHomes'
import { bulkGposNursingHomes } from '../generated/clients/bulkGposNursingHomes'
import { bulkDealsNursingHomes } from '../generated/clients/bulkDealsNursingHomes'
import { assignNursingHomesToCampaign } from '../generated/clients/assignNursingHomesToCampaign'
import { exportNursingHomesV2 } from '../generated/clients/exportNursingHomesV2'

import type {
  GetNursingHomeQueryResponse,
  GetNursingHome404,
} from '../generated/types/GetNursingHome'
import type { BulkTagsNursingHomesMutationRequest } from '../generated/types/BulkTagsNursingHomes'
import type { BulkDistributorsNursingHomesMutationRequest } from '../generated/types/BulkDistributorsNursingHomes'
import type { BulkGposNursingHomesMutationRequest } from '../generated/types/BulkGposNursingHomes'
import type { BulkDealsNursingHomesMutationRequest } from '../generated/types/BulkDealsNursingHomes'
import type { AssignNursingHomesToCampaignMutationRequest } from '../generated/types/AssignNursingHomesToCampaign'
import type { ExportNursingHomesV2MutationRequest } from '../generated/types/ExportNursingHomesV2'

// ── Hand-written query hooks ───────────────────────────────────────

/**
 * Get nursing home detail.
 *
 * Hand-written wrapper around the generated `getNursingHome` client because
 * the legacy hook skips retries on 404 — without that predicate, React
 * Query retries 3× before surrendering, which delays "not found" feedback
 * in the UI. The wrapper accepts an optional id (so callers can pass
 * `undefined` before route resolution) and disables the query until the
 * id arrives.
 */
export function useGetNursingHome(
  nursingHomeId?: number | string,
  options: {
    query?: Omit<
      UseQueryOptions<
        GetNursingHomeQueryResponse,
        AxiosError<GetNursingHome404>
      >,
      'queryKey' | 'queryFn' | 'enabled' | 'retry'
    >
  } = {}
) {
  const { query = {} } = options
  // The backend route is `<str:nursinghome_id>`; callers pass the numeric
  // ContactCompany id, so coerce to the string the generated client expects.
  const id = nursingHomeId != null ? String(nursingHomeId) : undefined
  return useQuery<GetNursingHomeQueryResponse, AxiosError<GetNursingHome404>>({
    queryKey:
      id !== undefined
        ? _getNursingHomeQueryKey({ nursinghome_id: id })
        : ['nursinghome', nursingHomeId],
    queryFn: async ({ signal }) =>
      getNursingHome({ nursinghome_id: id as string }, { signal }),
    enabled: id !== undefined,
    retry: (failureCount, error) =>
      axios.isAxiosError(error) && error?.response?.status === 404
        ? false
        : failureCount < 3,
    ...query,
  })
}

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

export function useBulkTagsNursingHomes() {
  return useInvalidatingMutation('bulkTagsNursingHomes', invalidationRegistry, {
    mutationFn: (variables: { data: BulkTagsNursingHomesMutationRequest }) =>
      bulkTagsNursingHomes(variables.data),
  })
}

export function useBulkDistributorsNursingHomes() {
  return useInvalidatingMutation(
    'bulkDistributorsNursingHomes',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: BulkDistributorsNursingHomesMutationRequest
      }) => bulkDistributorsNursingHomes(variables.data),
    }
  )
}

export function useBulkGposNursingHomes() {
  return useInvalidatingMutation('bulkGposNursingHomes', invalidationRegistry, {
    mutationFn: (variables: { data: BulkGposNursingHomesMutationRequest }) =>
      bulkGposNursingHomes(variables.data),
  })
}

export function useBulkDealsNursingHomes() {
  return useInvalidatingMutation(
    'bulkDealsNursingHomes',
    invalidationRegistry,
    {
      mutationFn: (variables: { data: BulkDealsNursingHomesMutationRequest }) =>
        bulkDealsNursingHomes(variables.data),
    }
  )
}

export function useAssignNursingHomesToCampaign() {
  return useInvalidatingMutation(
    'assignNursingHomesToCampaign',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: AssignNursingHomesToCampaignMutationRequest
      }) => assignNursingHomesToCampaign(variables.data),
    }
  )
}

export function useExportNursingHomesV2() {
  return useInvalidatingMutation('exportNursingHomesV2', invalidationRegistry, {
    mutationFn: (variables: { data: ExportNursingHomesV2MutationRequest }) =>
      exportNursingHomesV2(variables.data),
  })
}
